import "../../../../../../../../../styles/shared/stripe-credit-card-form.css";

// Redux
import { updateUser } from "../../../../../../../../../store/userSlice";
import { showSnackBar } from "../../../../../../../../../store/common/snackBarSlice";

// APIs
import {
  chargeAmount,
  confirmAddCardWithCharge,
} from "../../../../../../../../../services/CreditCard/stripe.services";

// Stripe
import { PaymentElement } from "@stripe/react-stripe-js";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import { useDispatch, useSelector } from "react-redux";

const PAYMENT_FEE_PERCENTAGE = 0.03; // 3%
const PAYMENT_FEE_LABEL = `${(PAYMENT_FEE_PERCENTAGE * 100).toFixed(
  0
)}% Payment Processing Fee`;

// Helpers
const toCents = (value) => Math.round((parseFloat(value) || 0) * 100);
const fromCents = (cents) => (cents / 100).toFixed(2);

export default function PendingBalanceCardForm({
  balance,
  SetStepCheck,
  cards,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  const [isProcessing, setIsProcessing] = useState(false);
  const [addOtherCard, setAddOtherCard] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState(null);

  // Calculate fee and display string
  const { displayChargeString, chargeAmountStr } = useMemo(() => {
    const balCents = toCents(balance);
    const feeCents = Math.round(balCents * PAYMENT_FEE_PERCENTAGE);
    const totalCents = balCents + feeCents;

    return {
      displayChargeString: `${fromCents(balCents)} + ${fromCents(
        feeCents
      )} (${PAYMENT_FEE_LABEL}) = ${fromCents(totalCents)}`,
      chargeAmountStr: fromCents(totalCents),
    };
  }, [balance]);

  // Select default card
  useEffect(() => {
    const defaultCard = cards.find((c) => c.card?.is_default);
    if (defaultCard) setPaymentMethodId(defaultCard.id);
  }, [cards]);

  // Update user balance
  const updateBalance = useCallback(
    (newBalance) => {
      SetStepCheck((prev) => ({
        ...prev,
        SingleQuoteData: {
          ...prev.SingleQuoteData,
          user_balance: newBalance,
        },
      }));
      dispatch(updateUser({ balance: newBalance }));
    },
    [SetStepCheck, dispatch]
  );

  // Charge existing card
  const processCharge = useCallback(
    async (pmId) => {
      setIsProcessing(true);
      try {
        const response = await chargeAmount(balance, pmId);
        if (response.error) {
          dispatch(
            showSnackBar({
              text: response.error.response?.data?.message || "Payment failed.",
              severity: "error",
            })
          );
        } else {
          dispatch(
            showSnackBar({
              text: "Your balance has been updated via credit card.",
              severity: "success",
            })
          );
          updateBalance(response?.data?.result?.balance);
        }
      } catch (error) {
        dispatch(
          showSnackBar({
            text: "Payment failed. Please try again.",
            severity: "error",
          })
        );
      } finally {
        setIsProcessing(false);
      }
    },
    [chargeAmountStr, dispatch, updateBalance]
  );

  // Add new card and charge
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);

    const response = await stripe.confirmSetup({
      elements,
      confirmParams: {},
      redirect: "if_required",
    });

    if (response.error) {
      dispatch(
        showSnackBar({
          text: response.error.message,
          severity: "error",
        })
      );
      setIsProcessing(false);
      return;
    }

    if (response.setupIntent.payment_method) {
      try {
        const result = await confirmAddCardWithCharge(
          response.setupIntent.payment_method,
          balance
        );
        dispatch(
          showSnackBar({
            text: "Your balance has been updated via credit card.",
            severity: "success",
          })
        );
        updateBalance(result?.data?.result?.balance);
      } catch {
        dispatch(
          showSnackBar({
            text: "Unable to add card or charge amount.",
            severity: "error",
          })
        );
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const isDisabled = isProcessing || (!stripe && !user.card_last_four);

  return (
    <div className="block relative px-1 w-full pb-4 min-h-fit">
      {/* Balance Display */}
      <div className="mb-2">
        <label className="block font-medium text-ui-text mb-1">
          Outstanding Amount (CAD)
        </label>
        <input
          type="text"
          value={balance}
          className="w-full border rounded px-3 py-2 bg-ui-surface-sunken"
          disabled
        />
      </div>

      <div className="mb-4">
        <label className="block font-bold text-ui-text mb-1">
          Total Amount Charged (CAD)
        </label>
        <input
          type="text"
          value={displayChargeString}
          className="w-full border rounded px-3 py-2 font-bold bg-ui-surface-sunken"
          disabled
        />
      </div>

      {/* Payment Section */}
      <div className="space-y-3">
        {(!user.card_last_four || addOtherCard) && (
          <PaymentElement id="payment-element" />
        )}

        {cards.length > 0 && !addOtherCard && (
          <>
            {cards.map((card) => (
              <div
                key={card.id}
                className="border rounded p-3 flex items-center justify-between"
              >
                <div>
                  <input
                    type="radio"
                    name="radio_card"
                    onChange={() => setPaymentMethodId(card.id)}
                    defaultChecked={card.card.is_default}
                    className="mr-2"
                  />
                  <span className="font-semibold capitalize">
                    {card.card.brand}
                  </span>
                  <div className="text-sm text-ui-text-muted">
                    xxxx xxxx xxxx {card.card.last4}
                  </div>
                  {card.card.is_default && (
                    <span className="text-xs text-state-success font-semibold">
                      Default
                    </span>
                  )}
                </div>
              </div>
            ))}
            <div className="flex items-center mt-2">
              <input
                type="radio"
                name="radio_card"
                onChange={() => setAddOtherCard(true)}
                className="mr-2"
              />
              <span
                onClick={() => setAddOtherCard(true)}
                className="text-brand cursor-pointer"
              >
                Pay using a new card
              </span>
            </div>
          </>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end mt-4 space-x-2">
        <button
          disabled={isDisabled}
          onClick={
            !user.card_last_four || addOtherCard
              ? handleSubmit
              : () => processCharge(paymentMethodId)
          }
          className={`px-4 py-2 rounded text-white ${
            isDisabled
              ? "bg-ui-text-subtle cursor-not-allowed"
              : "bg-brand hover:bg-brand-hover"
          }`}
        >
          {isProcessing ? "Processing..." : "Pay now"}
        </button>
        <button
          onClick={() =>
            SetStepCheck((prev) => ({
              ...prev,
              Final_Order: false,
            }))
          }
          className="px-4 py-2 rounded bg-ui-border hover:bg-ui-text-subtle"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
